import React, { useEffect, useState, useMemo, useDeferredValue } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { exportToCSV } from "@/lib/csvExport";

/**
 * Generic Master List Page.
 * Config supplies: title, endpoint, fields[], columns[]
 */
export default function MasterPage({ title, endpoint, fields, columns, testidPrefix, onFormChange, extraHeaderActions, reloadKey, serverSearch = false }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const debounceRef = React.useRef(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      if (serverSearch) {
        const url = query ? `${endpoint}?q=${encodeURIComponent(query)}&limit=200` : `${endpoint}?limit=200`;
        const [listResp, countResp] = await Promise.all([
          api.get(url),
          api.get(`${endpoint}/count${query ? `?q=${encodeURIComponent(query)}` : ""}`).catch(() => ({ data: { count: 0 } })),
        ]);
        setRows(listResp.data);
        setTotalCount(countResp.data?.count ?? listResp.data.length);
      } else {
        const { data } = await api.get(endpoint);
        setRows(data);
        setTotalCount(data.length);
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(""); /* eslint-disable-next-line */ }, [reloadKey, endpoint, serverSearch]);

  // Server-side search: debounce API call when q changes
  useEffect(() => {
    if (!serverSearch) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q), 250);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [q, serverSearch]);

  useEffect(() => { if (onFormChange) onFormChange(form); }, [form, onFormChange]);

  const openNew = () => {
    setEditing(null);
    const empty = {};
    fields.forEach(f => empty[f.name] = f.default ?? "");
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    const filled = {};
    fields.forEach(f => filled[f.name] = row[f.name] ?? f.default ?? "");
    setForm(filled);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // coerce types
      fields.forEach(f => {
        if (f.type === "number") payload[f.name] = parseFloat(payload[f.name] || 0);
        if (f.name.endsWith("_inclusive")) payload[f.name] = payload[f.name] === "true" || payload[f.name] === true;
      });
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false);
      load(q);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${title.slice(0, -1)}?`)) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Deleted");
      load(q);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deferredQ = useDeferredValue(q);
  const filtered = useMemo(() => {
    if (serverSearch) return rows;  // server already filtered
    if (!deferredQ) return rows;
    const s = deferredQ.toLowerCase();
    const keys = Array.from(new Set([...columns.map(c => c.key), ...fields.map(f => f.name)]));
    const out = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      for (let j = 0; j < keys.length; j++) {
        if (String(r[keys[j]] ?? "").toLowerCase().includes(s)) {
          out.push(r);
          break;
        }
      }
      if (out.length >= 500) break;
    }
    return out;
  }, [deferredQ, rows, columns, fields, serverSearch]);

  return (
    <div className="space-y-4" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your {title.toLowerCase()} records.</p>
        </div>
        <div className="flex gap-2">
          {extraHeaderActions}
          <Button onClick={openNew} data-testid={`${testidPrefix}-add-button`} className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
            <Plus size={16} className="mr-1" weight="bold" /> New {title.slice(0, -1)}
          </Button>
        </div>
      </div>

      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid={`${testidPrefix}-search`} />
          </div>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(filtered, columns.map(c => ({ label: c.label, key: c.key, get: c.exportGet })), title.toLowerCase())} data-testid={`${testidPrefix}-export`}>
            <Download size={14} className="mr-1" /> Export CSV
          </Button>
          <div className="text-xs text-muted-foreground tabular-nums">
            {loading ? "Loading…" : (serverSearch ? `${filtered.length} of ${totalCount.toLocaleString()}` : `${filtered.length} records`)}
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map(c => (
                  <TableHead key={c.key} className={c.className}>{c.label}</TableHead>
                ))}
                <TableHead className="text-right w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.slice(0, 200).map(row => (
                <TableRow key={row.id} data-testid={`${testidPrefix}-row-${row.id}`}>
                  {columns.map(c => (
                    <TableCell key={c.key} className={c.className}>
                      {c.render ? c.render(row) : row[c.key]}
                    </TableCell>
                  ))}
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-secondary mr-1" data-testid={`${testidPrefix}-edit-${row.id}`}><PencilSimple size={15} /></button>
                    <button onClick={() => remove(row)} className="p-1.5 rounded hover:bg-destructive/10 text-destructive" data-testid={`${testidPrefix}-delete-${row.id}`}><Trash size={15} /></button>
                  </TableCell>
                </TableRow>
              ))}
              {(serverSearch ? totalCount > filtered.length : filtered.length > 200) && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-3 text-xs text-muted-foreground bg-secondary/30">
                  Showing {filtered.length} of {(serverSearch ? totalCount : filtered.length).toLocaleString()} matches — refine search to narrow down.
                </TableCell></TableRow>
              )}
              {!filtered.length && (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-12 text-muted-foreground">No records. Add your first {title.slice(0, -1).toLowerCase()}.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit" : "New"} {title.slice(0, -1)}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {fields.map(f => (
              <div key={f.name} className={f.full || f.type === "section" ? "col-span-2" : ""}>
                {f.type === "section" ? (
                  <div className="mt-4 pb-1 border-b border-border flex items-center justify-between">
                    <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(24.6_95%_53.1%)]">{f.label}</div>
                    {f.action && (
                      <button type="button" onClick={() => f.action(form, setForm)} className="text-xs text-blue-600 hover:underline" data-testid={`${testidPrefix}-action-${f.name}`}>
                        {f.actionLabel || "Action"}
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <Label>{f.label}{f.required && <span className="text-destructive">*</span>}</Label>
                {f.type === "select" ? (
                  <select
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm"
                    data-testid={`${testidPrefix}-field-${f.name}`}
                  >
                    {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md bg-card text-sm"
                    data-testid={`${testidPrefix}-field-${f.name}`}
                  />
                ) : f.type === "datalist" ? (
                  <>
                    <Input
                      list={f.listId}
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      required={f.required}
                      data-testid={`${testidPrefix}-field-${f.name}`}
                      placeholder="Select or type new"
                    />
                    <datalist id={f.listId}>
                      {(f.options || []).map(o => <option key={o} value={o} />)}
                    </datalist>
                  </>
                ) : (
                  <Input
                    type={f.type || "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    required={f.required}
                    data-testid={`${testidPrefix}-field-${f.name}`}
                  />
                )}
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid={`${testidPrefix}-cancel`}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid={`${testidPrefix}-save`} className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
              {busy ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
